import { CommonModule } from '@angular/common';
import { Component, computed, ElementRef, HostListener, OnDestroy, OnInit, signal, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { Subject, takeUntil } from 'rxjs';
import { LateFeeConfig, LateFeeConfigRequest, LateFeeType } from '../../../core/models/late-fee-config.model';
import { LateFeeConfigService } from '../../../core/services/api-services/late-fee-config.service';
import { CompanyService } from '../../../core/services/api-services/company.service';
import { AuthService } from '../../../core/services/client-services/auth.service';
import { BreadcrumbComponent, BreadcrumbItem } from '../../../shared/components/breadcrumb/breadcrumb.component';
import { BreadcrumbService } from '../../../shared/components/breadcrumb/service/breadcrumb.service';
import { FilterPanelComponent } from '../../../shared/components/filter-panel/filter-panel.component';
import { FilterField, FilterResult } from '../../../shared/components/filter-panel/model/filter-field.model';
import { PageEvent, PaginatorComponent } from '../../../shared/components/paginator/paginator.component';
import { FormattedNumberInputComponent } from '../../../shared/components/formatted-number-input/formatted-number-input.component';

declare const bootstrap: any;

interface ConfigGroup {
  companyId: number;
  lateFeeTypeId: number;
  primary: LateFeeConfig;
  historical: LateFeeConfig[];
}

interface CompanySection {
  companyId: number;
  companyName: string | null;
  groups: ConfigGroup[];
}

@Component({
  selector: 'app-late-fee-configuration',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    BreadcrumbComponent,
    PaginatorComponent,
    FilterPanelComponent,
    FormattedNumberInputComponent,
  ],
  templateUrl: './late-fee-configuration.component.html',
  styleUrl: './late-fee-configuration.component.css',
})
export class LateFeeConfigurationComponent implements OnInit, OnDestroy {

  breadcrumbItems: BreadcrumbItem[] = [];
  private destroy$ = new Subject<void>();

  @ViewChild('configOffcanvasEl')  offcanvasElementRef!: ElementRef;
  @ViewChild('deleteModalEl')       deleteModalRef!: ElementRef;
  @ViewChild('companySearchInput')  companySearchInputRef!: ElementRef;

  private offcanvasInstance: any = null;
  private deleteModalInstance: any = null;

  // ── Table state ───────────────────────────────────────────────
  configs: LateFeeConfig[] = [];
  configGroups: ConfigGroup[] = [];
  companySections: CompanySection[] = [];
  filteredSections: CompanySection[] = [];
  private expandedGroups = new Set<string>();
  isLoading = false;
  errorMessage = '';

  // ── Pagination ────────────────────────────────────────────────
  currentPage = 1;
  pageSize = 5;
  totalConfigs = 0;

  // ── Active server-side filters ─────────────────────────────────
  activeCompanyId: number | null = null;
  activeIsCurrent: boolean | null = null;
  activeIsActive: boolean | null = null;

  // ── Offcanvas form ────────────────────────────────────────────
  mode: 'add' | 'edit' = 'add';
  configForm!: FormGroup;
  isSaving = false;
  selectedConfigId: number | null = null;
  // ── Edit snapshot (for no-changes detection) ───────────────────────
  private originalEditSnapshot: LateFeeConfigRequest | null = null;
  // ── Delete state ──────────────────────────────────────────────
  pendingDeleteId: number | null = null;
  isDeleting = false;

  // ── Lookup data ───────────────────────────────────────────────
  lateFeeTypes: LateFeeType[] = [];

  // ── Search ──────────────────────────────────────────────────
  searchText = '';

  // ── Employer list (for form offcanvas dropdown) ──────────────────────
  companies: any[] = [];
  isCompaniesLoading = false;

  // Form (offcanvas) dropdown
  formSelectedCompanyId: number | null = null;
  formCompanySearchTerm = signal('');
  formCompanyDropdownOpen = false;
  formFilteredCompanies = computed(() => {
    const term = this.formCompanySearchTerm().toLowerCase().trim();
    if (!term) return this.companies;
    return this.companies.filter(c =>
      c.companyName.toLowerCase().includes(term) ||
      String(c.companyId).includes(term)
    );
  });

  // ── Filter panel ──────────────────────────────────────────────
  filterFields: FilterField[] = [
    {
      type: 'radio-group',
      controlName: 'filterType',
      label: 'Filter By:',
      defaultValue: 'companyId',
      options: [
        {
          value: 'companyId',
          label: 'Company ID',
          field: {
            type: 'text',
            controlName: 'companyId',
            placeholder: 'Enter Company ID',
            hint: 'Example: 9412265199 (10 digits)',
            validators: [Validators.required, Validators.pattern(/^\d{10}$/)],
          },
        },
        {
          value: 'isActive',
          label: 'Status',
          field: {
            type: 'select',
            controlName: 'isActive',
            placeholder: 'Select status',
            options: [
              { label: 'Active', value: 'true' },
              { label: 'Inactive', value: 'false' },
            ],
          },
        },
      ],
    },
  ];

  constructor(
    private fb: FormBuilder,
    private lateFeeConfigService: LateFeeConfigService,
    private breadcrumbService: BreadcrumbService,
    private toastr: ToastrService,
    public authService: AuthService,
    private companyService: CompanyService,
  ) {}

  ngOnInit(): void {

    // Subscribe to breadcrumb changes
    this.breadcrumbService.breadcrumbs$
      .pipe(takeUntil(this.destroy$))
      .subscribe(items => {
        this.breadcrumbItems = items;
      });

    // Set breadcrumbs for this page
    this.breadcrumbService.setBreadcrumbs([
      { label: 'Dashboard', url: '/dashboard', icon: 'bi bi-house-door' },
      { label: 'Configuration', icon: 'bi bi-gear' },
      { label: 'Late Fee Configuration', icon: 'bi bi-cash-coin' }
    ]);

    this.buildForm();
    this.loadLateFeeTypes();
    this.loadCompanies();
    this.loadConfigs();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ── Form ──────────────────────────────────────────────────────

  private buildForm(): void {
    this.configForm = this.fb.group({
      companyId:    [null, Validators.required],
      lateFeeTypeId:[null, Validators.required],
      lateFee:      [null, [Validators.required, Validators.min(0.01)]],
      isPercentage: [false],
    });
  }

  // ── Data loading ──────────────────────────────────────────────

  private loadLateFeeTypes(): void {
    this.lateFeeConfigService.getLateFeeTypes()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          if (res.success) this.lateFeeTypes = res.data;
        },
        error: () => this.toastr.error('Failed to load late fee types.'),
      });
  }

  loadConfigs(page: number = this.currentPage, size: number = this.pageSize): void {
    this.isLoading = true;
    this.errorMessage = '';
    this.expandedGroups.clear();

    this.lateFeeConfigService
      .getLateFeeConfigsPaginated(
        page,
        size,
        this.activeCompanyId,
        this.activeIsCurrent,
        this.activeIsActive,
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.isLoading = false;
          if (res.success) {
            this.configs = res.data;
            this.totalConfigs = res.totalCount;
          } else {
            this.configs = [];
            this.totalConfigs = 0;
          }
          this.buildGroups();
        },
        error: () => {
          this.isLoading = false;
          this.configs = [];
          this.buildGroups();
          this.errorMessage = 'Failed to load configurations.';
        },
      });
  }

  // ── Pagination ─────────────────────────────────────────────────

  onPageChange(event: PageEvent): void {
    this.currentPage = event.page;
    this.pageSize    = event.pageSize;
    this.loadConfigs(this.currentPage, this.pageSize);
  }

  // ── Filter panel ───────────────────────────────────────────────

  onFilter(result: FilterResult): void {
    this.currentPage = 1;

    this.activeCompanyId = result['companyId'] != null
      ? Number(result['companyId'])
      : null;

    this.activeIsCurrent = null;

    this.activeIsActive = result['isActive'] != null
      ? result['isActive'] === 'true'
      : null;

    this.loadConfigs();
  }

  onClearFilter(): void {
    this.currentPage     = 1;
    this.activeCompanyId = null;
    this.activeIsCurrent = null;
    this.activeIsActive  = null;
    this.loadConfigs();
  }

  performSearch(): void {
    this.applySearch();
  }

  private applySearch(): void {
    const term = this.searchText.trim().toLowerCase();
    if (!term) {
      this.filteredSections = this.companySections;
      return;
    }
    this.filteredSections = this.companySections.filter(s =>
      String(s.companyId).includes(term) ||
      (s.companyName ?? '').toLowerCase().includes(term)
    );
  }

  // ── Company dropdown (form offcanvas) ───────────────────────────────

  private loadCompanies(): void {
    this.isCompaniesLoading = true;
    this.companyService.getExistingCompanies().pipe(takeUntil(this.destroy$)).subscribe({
      next: (response: any) => {
        this.companies = response?.success ? (response.data ?? []) : [];
        this.isCompaniesLoading = false;
      },
      error: () => {
        this.companies = [];
        this.isCompaniesLoading = false;
      },
    });
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (!target.closest('.searchable-dropdown')) {
      this.formCompanyDropdownOpen = false;
    }
  }

  // Form company dropdown methods
  getFormSelectedCompanyName(): string {
    if (!this.formSelectedCompanyId) return '';
    const found = this.companies.find(c => c.companyId === this.formSelectedCompanyId);
    return found ? `${found.companyName} (${found.companyId})` : '';
  }

  openFormCompanyDropdown(): void {
    this.formCompanyDropdownOpen = true;
    this.formCompanySearchTerm.set('');
  }

  onFormCompanySearchChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.formCompanySearchTerm.set(input.value);
    this.formCompanyDropdownOpen = true;
  }

  selectFormCompany(company: any): void {
    this.formSelectedCompanyId = company ? company.companyId : null;
    this.configForm.patchValue({ companyId: this.formSelectedCompanyId });
    this.formCompanySearchTerm.set('');
    this.formCompanyDropdownOpen = false;
  }

  onFormCompanyBlur(): void {
    setTimeout(() => {
      this.formCompanyDropdownOpen = false;
      this.formCompanySearchTerm.set('');
    }, 200);
  }

  // ── Offcanvas ──────────────────────────────────────────────────

  openAddForm(): void {
    this.mode = 'add';
    this.selectedConfigId = null;
    this.formSelectedCompanyId = null;
    this.formCompanySearchTerm.set('');
    this.formCompanyDropdownOpen = false;
    this.configForm.reset({ isPercentage: false, companyId: null });
    this.showOffcanvas();
  }

  openEditForm(config: LateFeeConfig): void {
    this.mode = 'edit';
    this.selectedConfigId = config.lateFeeConfigId;
    this.formSelectedCompanyId = config.companyId;
    this.formCompanySearchTerm.set('');
    this.formCompanyDropdownOpen = false;
    this.configForm.patchValue({
      companyId:     config.companyId,
      lateFeeTypeId: config.lateFeeTypeId,
      lateFee:       config.lateFee,
      isPercentage:  config.isPercentage,
    });
    this.originalEditSnapshot = {
      companyId:     config.companyId,
      lateFeeTypeId: config.lateFeeTypeId,
      lateFee:       config.lateFee,
      isPercentage:  config.isPercentage,
    };
    this.showOffcanvas();
  }

  private showOffcanvas(): void {
    if (!this.offcanvasInstance) {
      this.offcanvasInstance = new bootstrap.Offcanvas(this.offcanvasElementRef.nativeElement, {
        backdrop: 'static',
        keyboard: false,
      });
    }
    this.offcanvasInstance.show();
  }

  closeOffcanvas(): void {
    this.offcanvasInstance?.hide();
    this.formCompanyDropdownOpen = false;
    this.formCompanySearchTerm.set('');
  }

  // ── Save (create / update) ────────────────────────────────────

  onSave(): void {
    if (this.configForm.invalid) {
      this.configForm.markAllAsTouched();
      return;
    }

    const payload: LateFeeConfigRequest = {
      companyId:     Number(this.configForm.value.companyId),
      lateFeeTypeId: Number(this.configForm.value.lateFeeTypeId),
      lateFee:       Number(this.configForm.value.lateFee),
      isPercentage:  Boolean(this.configForm.value.isPercentage),
    };

    if (this.mode === 'edit' && this.originalEditSnapshot) {
      const s = this.originalEditSnapshot;
      const noChanges =
        s.companyId     === payload.companyId &&
        s.lateFeeTypeId === payload.lateFeeTypeId &&
        s.isPercentage  === payload.isPercentage &&
        Number(s.lateFee.toFixed(2)) === Number(payload.lateFee.toFixed(2));
      if (noChanges) {
        this.toastr.info('No changes detected. Please modify at least one field before saving.');
        return;
      }
    }

    this.isSaving = true;

    const call = this.mode === 'add'
      ? this.lateFeeConfigService.createLateFeeConfig(payload)
      : this.lateFeeConfigService.updateLateFeeConfig(this.selectedConfigId!, payload);

    call.pipe(takeUntil(this.destroy$)).subscribe({
      next: (res) => {
        this.isSaving = false;
        if (res.success) {
          this.toastr.success(
            this.mode === 'add'
              ? 'Late fee configuration created successfully.'
              : 'Late fee configuration updated. A new version has been created.',
          );
          this.closeOffcanvas();
          this.loadConfigs();
        } else {
          this.toastr.error(res.message || 'Failed to save configuration.');
        }
      },
      error: () => {
        this.isSaving = false;
        this.toastr.error('An unexpected error occurred.');
      },
    });
  }

  // ── Soft Delete ────────────────────────────────────────────────

  confirmDelete(lateFeeConfigId: number): void {
    this.pendingDeleteId = lateFeeConfigId;
    if (!this.deleteModalInstance) {
      this.deleteModalInstance = new bootstrap.Modal(this.deleteModalRef.nativeElement);
    }
    this.deleteModalInstance.show();
  }

  cancelDelete(): void {
    this.pendingDeleteId = null;
    this.deleteModalInstance?.hide();
  }

  executeDelete(): void {
    if (!this.pendingDeleteId) return;

    this.isDeleting = true;
    this.lateFeeConfigService.deleteLateFeeConfig(this.pendingDeleteId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.isDeleting = false;
          this.deleteModalInstance?.hide();
          this.pendingDeleteId = null;
          if (res.success) {
            this.toastr.success('Configuration deactivated successfully.');
            this.loadConfigs();
          } else {
            this.toastr.error(res.message || 'Failed to deactivate configuration.');
          }
        },
        error: () => {
          this.isDeleting = false;
          this.toastr.error('An unexpected error occurred.');
        },
      });
  }

  // ── Grouped table ───────────────────────────────────────────────

  private buildGroups(): void {
    const map = new Map<string, LateFeeConfig[]>();
    for (const config of this.configs) {
      const key = `${config.companyId}-${config.lateFeeTypeId}`;
      const group = map.get(key) ?? [];
      group.push(config);
      map.set(key, group);
    }
    this.configGroups = Array.from(map.entries()).map(([, records]) => {
      records.sort((a, b) => {
        if (a.isCurrent && !b.isCurrent) return -1;
        if (!a.isCurrent && b.isCurrent) return 1;
        return new Date(b.createdDateUtc).getTime() - new Date(a.createdDateUtc).getTime();
      });
      const primary = records[0];
      return {
        companyId: primary.companyId,
        lateFeeTypeId: primary.lateFeeTypeId,
        primary,
        historical: records.slice(1),
      };
    });

    // Build company sections (second-level grouping by companyId)
    const sectionMap = new Map<number, ConfigGroup[]>();
    for (const group of this.configGroups) {
      const arr = sectionMap.get(group.companyId) ?? [];
      arr.push(group);
      sectionMap.set(group.companyId, arr);
    }
    this.companySections = Array.from(sectionMap.entries()).map(([companyId, groups]) => ({
      companyId,
      companyName: groups[0]?.primary?.companyName ?? null,
      groups,
    }));
    this.applySearch();
  }

  toggleGroup(key: string): void {
    if (this.expandedGroups.has(key)) {
      this.expandedGroups.delete(key);
    } else {
      this.expandedGroups.add(key);
    }
  }

  isGroupExpanded(key: string): boolean {
    return this.expandedGroups.has(key);
  }

  // ── Helpers ────────────────────────────────────────────────────

  getGroupKey(group: ConfigGroup): string {
    return `${group.companyId}-${group.lateFeeTypeId}`;
  }

  getTotalConfigCount(section: CompanySection): number {
    return section.groups.reduce((sum, g) => sum + 1 + g.historical.length, 0);
  }

  getTypeLabelByTypeId(typeId: number): string {
    return this.lateFeeTypes.find(t => t.lateFeeTypeId === typeId)?.typeCode ?? String(typeId);
  }
}

